import { flexRender } from '@tanstack/react-table';
import * as React from 'react';

import { Svg90RingWithBg } from '@/icons/iconsComponent';

import { TDataTableProps, useDataTable } from '../model';

import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from './table';

export function DataTable<TData, TValue>({
  columns,
  data,
  cellClassName,
  containerClassName,
  fetchMoreOnBottomReached,
  isFetching = false,
  hasHeight = false,
  height,
  fitContainer = false,
}: TDataTableProps<TData, TValue>) {
  const { table, rows, rowVirtualizer, containerRef, onScroll } = useDataTable<TData, TValue>({
    data,
    columns,
    fetchMoreOnBottomReached,
  });

  return (
    <div ref={containerRef} className={containerClassName}>
      <div
        dir='rtl'
        onScrollCapture={onScroll}
        className='order-border rounded-md'
        style={{ overflow: 'auto', height }}
      >
        <Table className={`border-separate border-spacing-0${fitContainer ? ' table-fixed' : ''}`}>
          <TableHeader>
            {table.getHeaderGroups().map(({ id, headers }) => (
              <TableRow key={id}>
                {headers.map((header) => (
                  <TableHead key={header.id} className='border-y-[1px] border-border'>
                    {!header.isPlaceholder &&
                      flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>

          <TableBody>
            {rowVirtualizer.getVirtualItems().map(({ index }) => {
              const row = rows[index];

              return (
                <TableRow
                  key={row.id}
                  data-index={index}
                  data-state={row.getIsSelected() && 'selected'}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell
                      key={cell.id}
                      style={fitContainer ? undefined : { width: `${cell.column.getSize()}px` }}
                      className={`${
                        cellClassName ?? 'py-2'
                      } border-b border-border first:!rounded-s-sm last:!rounded-e-sm`}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              );
            })}
          </TableBody>

          <TableFooter className='flex items-center justify-center' />
        </Table>

        {!hasHeight && isFetching && (
          <div className='flex mx-auto h-[50px] w-full items-start justify-center pt-[16px]'>
            <Svg90RingWithBg className='text-pics' />
          </div>
        )}
      </div>
    </div>
  );
}
